/**
 * Agent push key API (R20 chat 4)
 *
 * The credential an external agent (a sim-racing feeder on the rig, a voice
 * agent, a script) uses to push values into one module's properties on one
 * channel. One key per (channel, module), minted beside the module's settings.
 *
 *   GET    /api/modules/{name}/agent-key/{channelId}  status — never the key
 *   POST   /api/modules/{name}/agent-key/{channelId}  mint or regenerate
 *   DELETE /api/modules/{name}/agent-key/{channelId}  revoke
 *
 * THE KEY IS RETURNED ONCE, by POST, and must not be stored, logged or put in
 * a toast by any caller. Lost → regenerate (the old key stops at once).
 *
 * The agent sends it as `Authorization: Bearer <key>` to `push_path` on the
 * PratBot host — never in the URL.
 */

import api from '@/api/client'

export interface AgentKeyStatus {
  exists: boolean
  /** Short property names an agent may push, e.g. ["race", "standings"]. */
  properties: string[]
  /** Path the agent POSTs to, e.g. "/api/agent/20/sim_racing". */
  push_path: string
  created_at: string | null
  regenerated_at: string | null
  /** Flushed at most once a minute. */
  last_used_at: string | null
}

export interface AgentKeyMinted {
  key: string
  regenerated: boolean
  properties: string[]
  push_path: string
}

const base = (moduleName: string, channelId: number) =>
  `/api/modules/${encodeURIComponent(moduleName)}/agent-key/${channelId}`

export function getAgentKey(moduleName: string, channelId: number): Promise<AgentKeyStatus> {
  return api.get<AgentKeyStatus>(base(moduleName, channelId))
}

export function mintAgentKey(moduleName: string, channelId: number): Promise<AgentKeyMinted> {
  return api.post<AgentKeyMinted>(base(moduleName, channelId))
}

export function revokeAgentKey(moduleName: string, channelId: number): Promise<{ revoked: boolean }> {
  return api.delete<{ revoked: boolean }>(base(moduleName, channelId))
}
